package features.booking

import dev.gitlive.firebase.auth.auth
import features.BaseViewModel
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import network.FirebaseClient
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.call.body
import io.ktor.http.ContentType
import io.ktor.http.contentType

data class PasajeroConPago(
    val pasajero: PasajeroInfo,
    val metodoPago: String = "",  // "efectivo", "spei", "oxxo", ""
    val pagoConfirmado: Boolean = true,
    // Punto de subida DE ESTA reserva (para agrupar por parada de subida).
    // Cuando una reserva no tiene puntoSubida propio → cae al del viaje.
    val puntoSubida: String = "",
    // Millis desde epoch en que se creó la reserva. Sirve para el badge "revisar"
    // (efectivo pendiente hace más de 48h). 0 si no viene guardado.
    val timestampReserva: Long = 0L,
    // Id de la reserva en RTDB. Necesario para confirmar el pago desde el chofer.
    val reservaId: String = ""
)

data class ViajeChofer(
    val ruta: String,
    val fecha: String,
    val horario: String,
    val vehiculo: String,
    val puntoSubida: String = "",
    val pasajeros: List<PasajeroInfo>,
    val pasajerosConPago: List<PasajeroConPago> = emptyList(),
    val totalAsientos: Int,
    val esHoy: Boolean
)
sealed class VerificacionResult {
    data class Exito(val asiento: Int, val pasajero: String, val ruta: String, val horario: String) : VerificacionResult()
    data class PagoPendiente(val asiento: Int, val pasajero: String, val ruta: String, val horario: String) : VerificacionResult()
    data class YaVerificado(val asiento: Int, val pasajero: String) : VerificacionResult()
    data class Error(val mensaje: String) : VerificacionResult()
}
class ChoferViewModel(
    private val bookingRepository: BookingRepository
) : BaseViewModel() {

    private val _viajesHoy = MutableStateFlow<List<ViajeChofer>>(emptyList())
    val viajesHoy: StateFlow<List<ViajeChofer>> = _viajesHoy.asStateFlow()

    private val _viajesManana = MutableStateFlow<List<ViajeChofer>>(emptyList())
    val viajesManana: StateFlow<List<ViajeChofer>> = _viajesManana.asStateFlow()

    // Layout del vehículo del viaje seleccionado (para el mapa visual del manifiesto).
    private val _layoutViajeSeleccionado = MutableStateFlow<VehiculoConfig?>(null)
    val layoutViajeSeleccionado: StateFlow<VehiculoConfig?> = _layoutViajeSeleccionado.asStateFlow()

    /**
     * nombre del punto (subida o bajada) -> link de Google Maps.
     *
     * Las coordenadas ya estaban en los catálogos pero el chofer solo veía el nombre del lugar:
     * tenía que saberse dónde queda "Plaza Zielo Zibata". Subidas y bajadas van en un solo mapa
     * porque el manifiesto agrupa por ambas y el nombre es único entre catálogos.
     */
    private val _mapsPorPunto = MutableStateFlow<Map<String, String>>(emptyMap())
    val mapsPorPunto: StateFlow<Map<String, String>> = _mapsPorPunto.asStateFlow()

    fun cargarUbicacionesDePuntos() {
        viewModelScope.launch {
            val acc = mutableMapOf<String, String>()
            listOf("catalogos/puntosSubidaInfo", "catalogos/paradasInfo").forEach { ruta ->
                try {
                    val snap = network.FirebaseClient.database.reference(ruta).valueEvents.first()
                    if (snap.exists) {
                        snap.children.forEach { c ->
                            val nombre = c.key ?: return@forEach
                            val lat = try { c.child("lat").value<Double>() } catch (_: Exception) { 0.0 }
                            val lng = try { c.child("lng").value<Double>() } catch (_: Exception) { 0.0 }
                            if (lat != 0.0 && lng != 0.0) acc[nombre] = "https://maps.google.com/?q=$lat,$lng"
                        }
                    }
                } catch (_: Exception) { /* sin coords: el manifiesto simplemente no muestra el link */ }
            }
            _mapsPorPunto.value = acc
        }
    }

    /**
     * Ruta completa en Google Maps: arranca en la primera subida, pasa por las demás y termina
     * en la última bajada. Los puntos van en el orden que trae el manifiesto.
     * "" si no hay al menos dos puntos con coordenadas.
     */
    fun urlRutaCompleta(subidas: List<String>, bajadas: List<String>): String {
        val coords = { nombre: String -> _mapsPorPunto.value[nombre]?.substringAfter("?q=") }
        val puntos = (subidas + bajadas).mapNotNull(coords)
        if (puntos.size < 2) return ""
        val origen = puntos.first()
        val destino = puntos.last()
        val intermedios = puntos.drop(1).dropLast(1)
        val wp = if (intermedios.isEmpty()) "" else "&waypoints=" + intermedios.joinToString("%7C")
        return "https://www.google.com/maps/dir/?api=1&origin=$origen&destination=$destino$wp&travelmode=driving"
    }

    fun cargarLayoutDelViaje(ruta: String, fecha: String, horario: String) {
        viewModelScope.launch {
            _layoutViajeSeleccionado.value = bookingRepository.obtenerLayoutDeViaje(ruta, fecha, horario)
        }
    }

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private var _notificacionHoyEnviada = false // FIX #7: flag anti-spam

    private val _nombreChofer = MutableStateFlow("")
    val nombreChofer: StateFlow<String> = _nombreChofer.asStateFlow()

    // Cuando admin+ ve el panel de un chofer específico (impersonación), el uid del
    // chofer objetivo se pasa por aquí en vez de usar el uid del auth actual.
    private var uidObjetivo: String? = null

    private val _viajeSeleccionado = MutableStateFlow<ViajeChofer?>(null)
    val viajeSeleccionado: StateFlow<ViajeChofer?> = _viajeSeleccionado.asStateFlow()

    /**
     * Modo espectador: admin+ ve el panel del chofer con [uid]+[nombre] indicados
     * (en vez de leer los del auth actual). Al salir, ChoferScreen llama
     * limpiarImpersonacion() para volver a comportamiento normal.
     */
    fun cargarViajesImpersonando(uid: String, nombre: String) {
        uidObjetivo = uid
        cargarViajesDelChofer(nombre)
    }

    fun limpiarImpersonacion() {
        uidObjetivo = null
    }

    fun cargarViajesDelChofer(nombreOEmail: String) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                // En modo impersonar (uidObjetivo != null), usamos el nombre pasado tal cual;
                // NO leemos del auth actual (ese es el admin, no el chofer objetivo).
                val uid = if (uidObjetivo != null) null else dev.gitlive.firebase.Firebase.auth.currentUser?.uid
                var nombreReal = nombreOEmail

                if (uid != null) {
                    // Intentar leer nombre del usuario (con y sin dos puntos por legacy)
                    try {
                        val snapUser = withTimeoutOrNull(10_000) {
                            FirebaseClient.database
                                .reference("usuarios/$uid")
                                .valueEvents.first()
                        }
                        if (snapUser != null && snapUser.exists) {
                            // Intentar "nombre" (nuevo) y "nombre:" (legacy)
                            val nombre = try { snapUser.child("nombre").value<String>() } catch (e: Exception) { null }
                                ?: try { snapUser.child("nombre:").value<String>() } catch (e: Exception) { null }
                            if (!nombre.isNullOrBlank()) {
                                nombreReal = nombre
                            }
                        }
                    } catch (e: Exception) {
                    }

                    if (nombreReal == nombreOEmail) {
                        val displayName = dev.gitlive.firebase.Firebase.auth.currentUser?.displayName
                        if (!displayName.isNullOrBlank()) {
                            nombreReal = displayName
                        }
                    }
                }

                _nombreChofer.value = nombreReal

                val fechaHoy = org.example.abigail.obtenerFechaHoy()
                val fechaManana = org.example.abigail.calcularFechaManana(fechaHoy)

                val viajesH = obtenerViajesParaFecha(nombreReal, fechaHoy, esHoy = true)
                val viajesM = obtenerViajesParaFecha(nombreReal, fechaManana, esHoy = false)

                _viajesHoy.value = viajesH
                _viajesManana.value = viajesM

                // FIX #7: Solo notificar si es la primera carga del día (evitar spam en cada refresh).
                // uidObjetivo != null = admin PREVISUALIZANDO el panel del chofer (modo espectador):
                // no se debe disparar la notificación de "viajes de hoy" del chofer.
                if (viajesH.isNotEmpty() && !_notificacionHoyEnviada && uidObjetivo == null) {
                    _notificacionHoyEnviada = true
                    val totalPasajeros = viajesH.sumOf { it.totalAsientos }
                    NotificacionViewModel.enviar(
                        tipo = "viaje_hoy",
                        titulo = "Viajes de hoy",
                        mensaje = "Tienes ${viajesH.size} viaje(s) hoy con $totalPasajeros pasajero(s) en total.",
                        destinatarioUid = dev.gitlive.firebase.Firebase.auth.currentUser?.uid ?: ""
                    )
                }

            } catch (e: Exception) {
            }
            _isLoading.value = false
        }
    }

    fun seleccionarViaje(viaje: ViajeChofer) {
        _viajeSeleccionado.value = viaje
    }

    fun regresarAMisViajes() {
        _viajeSeleccionado.value = null
    }

    fun refrescarViajes() {
        val nombre = _nombreChofer.value
        if (nombre.isNotBlank()) {
            cargarViajesDelChofer(nombre)
        }
    }

    private suspend fun obtenerViajesParaFecha(nombreChofer: String, fecha: String, esHoy: Boolean): List<ViajeChofer> {
        val db = FirebaseClient.database
        val resultado = mutableListOf<ViajeChofer>()

        try {
            val snapProgramados = withTimeoutOrNull(15_000) {
                db.reference("viajes_programados").valueEvents.first()
            }
            if (snapProgramados == null) {
                return emptyList()
            }
            if (!snapProgramados.exists) {
                return emptyList()
            }

            // Iterar rutas con children para evitar fallo de deserialización
            snapProgramados.children.forEach { rutaSnap ->
                val ruta = rutaSnap.key ?: return@forEach

                // Buscar la fecha específica dentro de esta ruta
                val fechaSnap = rutaSnap.child(fecha)
                if (!fechaSnap.exists) return@forEach

                // Iterar horarios de esa fecha
                fechaSnap.children.forEach { horarioSnap ->
                    try {
                        val horario = horarioSnap.key ?: return@forEach

                        val choferAsignado = try { horarioSnap.child("chofer").value<String>() } catch (e: Exception) { "" }
                        val choferUidViaje = try { horarioSnap.child("choferUid").value<String>() } catch (e: Exception) { "" }
                        val vehiculo = try { horarioSnap.child("vehiculo").value<String>() } catch (e: Exception) { "" }
                        val puntoSubida = try { horarioSnap.child("puntoSubida").value<String>() } catch (e: Exception) { "" }

                        // Match robusto: si el viaje tiene choferUid, comparar por uid; si no, por nombre (legacy).
                        // En modo impersonar (admin viendo panel de un chofer), uidObjetivo tiene el uid del target.
                        val miUid = uidObjetivo ?: (dev.gitlive.firebase.Firebase.auth.currentUser?.uid ?: "")
                        // Normalización de nombre: minúsculas + sin acentos + trim → "Juan Pérez" == "Juan Perez".
                        fun normalizar(s: String) = s.trim().lowercase()
                            .replace("á","a").replace("é","e").replace("í","i").replace("ó","o").replace("ú","u")
                            .replace("ñ","n").replace("ü","u")
                        val coincide = if (choferUidViaje.isNotBlank() && miUid.isNotBlank())
                            choferUidViaje == miUid
                        else
                            normalizar(choferAsignado) == normalizar(nombreChofer)

                        if (coincide) {
                            val (pasajeros, pasajerosConPago) = cargarPasajerosDelViaje(ruta, fecha, horario)

                            resultado.add(
                                ViajeChofer(
                                    ruta = ruta,
                                    fecha = fecha,
                                    horario = horario,
                                    vehiculo = vehiculo,
                                    puntoSubida = puntoSubida,
                                    pasajeros = pasajeros,
                                    pasajerosConPago = pasajerosConPago,
                                    totalAsientos = pasajeros.size,
                                    esHoy = esHoy
                                )
                            )
                        }
                    } catch (e: Exception) {
                    }
                }
            }
        } catch (e: Exception) {
        }

        return resultado.sortedBy { it.horario }
    }

    private suspend fun cargarPasajerosDelViaje(ruta: String, fecha: String, horario: String): Pair<List<PasajeroInfo>, List<PasajeroConPago>> {
        return try {
            val db = FirebaseClient.database
            val path = "viajes/$ruta/$fecha/$horario/reservas"
            val snapshot = db.reference(path).valueEvents.first()

            if (!snapshot.exists) {
                return Pair(emptyList(), emptyList())
            }

            val pasajeros = mutableListOf<PasajeroInfo>()
            val pasajerosConPago = mutableListOf<PasajeroConPago>()

            snapshot.children.forEach { reservaSnap ->
                // Leer metodo de pago + punto de subida + timestamp de la reserva.
                val metodoPago = try { reservaSnap.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                val pagoConfirmado = try { reservaSnap.child("pagoConfirmado").value<Boolean>() } catch (e: Exception) { true }
                val puntoSubidaReserva = try { reservaSnap.child("puntoSubida").value<String>() } catch (e: Exception) { "" }
                // Timestamp: se guarda como String millis (evita bug Long-objeto en JS).
                val timestampReserva = try {
                    reservaSnap.child("timestamp").value<String>().toLongOrNull() ?: 0L
                } catch (_: Exception) {
                    try { reservaSnap.child("timestamp").value<Long>() } catch (_: Exception) { 0L }
                }

                val pasajerosSnap = reservaSnap.child("pasajeros")
                if (pasajerosSnap.exists) {
                    pasajerosSnap.children.forEach { pSnap ->
                        try {
                            val info = PasajeroInfo.fromSnapshot(pSnap)
                            pasajeros.add(info)
                            pasajerosConPago.add(PasajeroConPago(
                                pasajero = info,
                                metodoPago = metodoPago,
                                pagoConfirmado = pagoConfirmado,
                                puntoSubida = puntoSubidaReserva,
                                timestampReserva = timestampReserva,
                                reservaId = reservaSnap.key ?: ""
                            ))
                        } catch (e: Exception) {
                        }
                    }
                }
            }

            Pair(pasajeros.sortedBy { it.asiento }, pasajerosConPago.sortedBy { it.pasajero.asiento })
        } catch (e: Exception) {
            Pair(emptyList(), emptyList())
        }
    }

    suspend fun verificarCodigoQR(codigoQR: String): VerificacionResult {
        return try {
            val db = FirebaseClient.database

            if (!codigoQR.startsWith("SMARTVAN-")) {
                return VerificacionResult.Error("Código QR no válido para SmartVan")
            }

            val fechaHoy = org.example.abigail.obtenerFechaHoy()
            val fechaManana = org.example.abigail.calcularFechaManana(fechaHoy)

            for (fecha in listOf(fechaHoy, fechaManana)) {
                for (viaje in (_viajesHoy.value + _viajesManana.value)) {
                    val path = "viajes/${viaje.ruta}/$fecha/${viaje.horario}/verificaciones/$codigoQR"
                    try {
                        val snap = db.reference(path).valueEvents.first()
                        if (snap.exists) {
                            val verificado = try { snap.child("verificado").value<Boolean>() } catch (e: Exception) { false }
                            val asiento = try { snap.child("asiento").value<Int>() } catch (e: Exception) { 0 }
                            val pasajero = try { snap.child("pasajero").value<String>() } catch (e: Exception) { "" }
                            val reservaId = try { snap.child("reservaId").value<String>() } catch (e: Exception) { "" }

                            if (verificado) {
                                return VerificacionResult.YaVerificado(asiento, pasajero)
                            }

                            // Check payment status from the reservation
                            var esPagoPendiente = false
                            if (reservaId.isNotBlank()) {
                                try {
                                    val reservaPath = "viajes/${viaje.ruta}/$fecha/${viaje.horario}/reservas/$reservaId"
                                    val reservaSnap = db.reference(reservaPath).valueEvents.first()
                                    if (reservaSnap.exists) {
                                        val metodoPago = try { reservaSnap.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                                        val pagoConfirmado = try { reservaSnap.child("pagoConfirmado").value<Boolean>() } catch (e: Exception) { true }
                                        if (metodoPago == "efectivo" && !pagoConfirmado) {
                                            esPagoPendiente = true
                                        }
                                    }
                                } catch (e: Exception) {
                                }
                            }

                            db.reference(path).child("verificado").setValue(true)
                            db.reference(path).child("horaAbordaje").setValue(
                                org.example.abigail.obtenerMilisegundosActuales().toString()
                            )

                            return if (esPagoPendiente) {
                                VerificacionResult.PagoPendiente(asiento, pasajero, viaje.ruta, viaje.horario)
                            } else {
                                VerificacionResult.Exito(asiento, pasajero, viaje.ruta, viaje.horario)
                            }
                        }
                    } catch (e: Exception) { /* seguir buscando */ }
                }
            }

            VerificacionResult.Error("Boleto no encontrado para los viajes de hoy o mañana")
        } catch (e: Exception) {
            VerificacionResult.Error("Error al verificar: ${e.message}")
        }
    }

    private val _mensajeChofer = MutableStateFlow<String?>(null)
    val mensajeChofer: StateFlow<String?> = _mensajeChofer.asStateFlow()
    fun limpiarMensaje() { _mensajeChofer.value = null }

    /**
     * Confirma el pago en efectivo desde el chofer. La CF valida:
     *  - rol chofer/chofer_admin (además de admin+/desarrollador).
     *  - guard 1h: chofer solo puede desde 1h antes del viaje.
     * Refresca el manifiesto local al éxito.
     */
    fun confirmarPagoEfectivo(viaje: ViajeChofer, reservaId: String, onResult: (ok: Boolean, msg: String?) -> Unit) {
        viewModelScope.launch {
            try {
                val client = network.sharedHttpClient
                val idToken = FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.AppConfig.VERSION_CODE))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(viaje.ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(viaje.fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(viaje.horario))
                        put("reservaId", kotlinx.serialization.json.JsonPrimitive(reservaId))
                    })
                }
                val response = client.post(network.CloudFunctions.CONFIRMAR_PAGO_EFECTIVO) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
                val txt = response.body<String>()
                val ok = txt.contains("\"success\":true") || txt.contains("\"success\": true")
                if (ok) {
                    _mensajeChofer.value = "Pago cobrado y confirmado."
                    refrescarViajes()
                    onResult(true, null)
                } else {
                    val errMsg = Regex("\"error\":\\s*\"([^\"]+)\"").find(txt)?.groupValues?.getOrNull(1)
                        ?: "No se pudo confirmar el pago."
                    _mensajeChofer.value = errMsg
                    onResult(false, errMsg)
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "ChoferViewModel.confirmarPagoEfectivo[$reservaId]")
                _mensajeChofer.value = "Error: ${e.message}"
                onResult(false, e.message)
            }
        }
    }

    suspend fun marcarAsistenciaManual(viaje: ViajeChofer, asiento: Int, asistio: Boolean) {
        try {
            val db = FirebaseClient.database
            val path = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/asistencia/$asiento"
            db.reference(path).setValue(
                mapOf(
                    "asistio" to asistio,
                    "manual" to true,
                    "hora" to org.example.abigail.obtenerMilisegundosActuales().toString()
                )
            )
        } catch (e: Exception) {
        }
    }

    // ========== RASTREO GPS ==========
    private var rastreoJob: Job? = null

    private val _rastreoActivo = MutableStateFlow(false)
    val rastreoActivo: StateFlow<Boolean> = _rastreoActivo.asStateFlow()

    private val _estadoViaje = MutableStateFlow("no_iniciado") // no_iniciado, en_curso, finalizado
    val estadoViaje: StateFlow<String> = _estadoViaje.asStateFlow()

    private val _ultimaUbicacion = MutableStateFlow<Pair<Double, Double>?>(null)
    val ultimaUbicacion: StateFlow<Pair<Double, Double>?> = _ultimaUbicacion.asStateFlow()

    fun iniciarViaje(viaje: ViajeChofer) {
        if (_rastreoActivo.value) return
        _rastreoActivo.value = true
        _estadoViaje.value = "en_curso"
        // Mantener pantalla encendida para que el GPS siga fluyendo en primer plano
        org.example.abigail.mantenerPantallaActiva(true)

        viewModelScope.launch {
            // Escribir estado inicial en Firebase
            val db = FirebaseClient.database
            val rastreoPath = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/rastreo"
            try {
                db.reference(rastreoPath).child("estado").setValue("en_curso")
                db.reference(rastreoPath).child("inicioViaje").setValue(
                    org.example.abigail.obtenerMilisegundosActuales().toString()
                )
            } catch (e: Exception) {
            }
            // #9: avisar a admin+ que el chofer arrancó el viaje.
            NotificacionViewModel.enviar(
                tipo = "viaje_iniciado",
                titulo = "Viaje iniciado",
                mensaje = "${nombreChofer.value} inició el viaje ${viaje.ruta} (${viaje.fecha} ${viaje.horario}).",
                destinatarioRol = "admin"
            )
        }

        // #10: gate de GPS por config (control de costos de Firebase). Default true = como hoy.
        // Si config/gpsChoferesActivo == false, el viaje inicia y notifica, pero NO se escribe
        // ubicación (ahorra escrituras RTDB). Pensado para cuando se usen GPS físicos.
        rastreoJob = viewModelScope.launch {
            val gpsActivo = try {
                val snap = FirebaseClient.database.reference("config/gpsChoferesActivo").valueEvents.first()
                if (snap.exists) snap.value<Boolean>() else true
            } catch (_: Exception) { true }
            if (!gpsActivo) { _rastreoActivo.value = true; return@launch }
            while (_rastreoActivo.value) {
                try {
                    val ubicacion = org.example.abigail.obtenerUbicacionActual()
                    if (ubicacion != null) {
                        _ultimaUbicacion.value = ubicacion
                        val db = FirebaseClient.database
                        val rastreoPath = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/rastreo"
                        db.reference(rastreoPath).child("lat").setValue(ubicacion.first)
                        db.reference(rastreoPath).child("lng").setValue(ubicacion.second)
                        db.reference(rastreoPath).child("ultimaActualizacion").setValue(
                            org.example.abigail.obtenerMilisegundosActuales().toString()
                        )
                    } else {
                    }
                } catch (e: Exception) {
                }
                delay(10_000) // cada 10 segundos
            }
        }
    }

    fun finalizarViaje(viaje: ViajeChofer) {
        _rastreoActivo.value = false
        _estadoViaje.value = "finalizado"
        rastreoJob?.cancel()
        rastreoJob = null
        org.example.abigail.mantenerPantallaActiva(false)

        viewModelScope.launch {
            try {
                val db = FirebaseClient.database
                val rastreoPath = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/rastreo"
                db.reference(rastreoPath).child("estado").setValue("finalizado")
                db.reference(rastreoPath).child("finViaje").setValue(
                    org.example.abigail.obtenerMilisegundosActuales().toString()
                )
                detectarNoShows(viaje)
            } catch (e: Exception) {
            }
        }
    }

    /**
     * Al finalizar el viaje: un asiento con boleto (verificación) que NO fue escaneado por QR
     * ni marcado presente a mano (asistencia.asistio) = no-show. El pasajero perdió su boleto
     * por no presentarse. NO se reasigna ni reembolsa (si lo piden, van a soporte); solo se
     * marca (verificaciones/{qr}/noShow) y se AVISA al admin.
     * Automático: si el chofer no registró a todos, pueden salir falsos no-shows — trade-off elegido.
     */
    private suspend fun detectarNoShows(viaje: ViajeChofer) {
        try {
            val db = FirebaseClient.database
            val base = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}"
            val verifSnap = db.reference("$base/verificaciones").valueEvents.first()
            if (!verifSnap.exists) return
            // Asientos marcados presentes a mano por el chofer (además del QR).
            val presentesManual = mutableSetOf<Int>()
            val asisSnap = db.reference("$base/asistencia").valueEvents.first()
            if (asisSnap.exists) {
                asisSnap.children.forEach { a ->
                    val asistio = try { a.child("asistio").value<Boolean>() } catch (_: Exception) { false }
                    val asiento = a.key?.toIntOrNull() ?: -1
                    if (asistio && asiento > 0) presentesManual.add(asiento)
                }
            }
            val noShows = mutableListOf<Pair<Int, String>>()
            val marcas = mutableMapOf<String, Any?>()
            verifSnap.children.forEach { v ->
                val verificado = try { v.child("verificado").value<Boolean>() } catch (_: Exception) { false }
                val asiento = try { v.child("asiento").value<Int>() } catch (_: Exception) { 0 }
                val presente = verificado || (asiento in presentesManual)
                if (!presente) {
                    val nombre = try { v.child("pasajero").value<String>() } catch (_: Exception) { "" }
                    noShows.add(asiento to nombre)
                    v.key?.let { marcas["$base/verificaciones/$it/noShow"] = true }
                }
            }
            if (noShows.isNotEmpty()) {
                if (marcas.isNotEmpty()) db.reference().updateChildren(marcas)
                val detalle = noShows.sortedBy { it.first }.joinToString(", ") {
                    "Asiento ${it.first}" + if (it.second.isNotBlank()) " (${it.second})" else ""
                }
                NotificacionViewModel.enviar(
                    tipo = "no_show",
                    titulo = "No-show en ${viaje.ruta}",
                    mensaje = "${noShows.size} pasajero(s) no se presentaron en ${viaje.ruta} · ${viaje.fecha} ${viaje.horario}: $detalle. " +
                        "Perdieron su boleto; si piden reasignación deben contactar a soporte.",
                    destinatarioRol = "admin"
                )
            }
        } catch (e: Exception) {
            network.CrashReporter.recordException(e, "ChoferViewModel.detectarNoShows")
        }
    }

    fun cargarEstadoRastreo(viaje: ViajeChofer) {
        viewModelScope.launch {
            try {
                val db = FirebaseClient.database
                val rastreoPath = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/rastreo"
                val snap = db.reference(rastreoPath).valueEvents.first()
                if (snap.exists) {
                    val estado = try { snap.child("estado").value<String>() } catch (e: Exception) { "no_iniciado" }
                    _estadoViaje.value = estado
                    if (estado == "en_curso") {
                        // Retomar rastreo si estaba en curso
                        _rastreoActivo.value = false // Reset para que iniciarViaje funcione
                        iniciarViaje(viaje)
                    }
                } else {
                    _estadoViaje.value = "no_iniciado"
                }
            } catch (e: Exception) {
                _estadoViaje.value = "no_iniciado"
            }
        }
    }

    suspend fun obtenerAsistencia(viaje: ViajeChofer): Map<Int, Boolean> {
        return try {
            val db = FirebaseClient.database
            val path = "viajes/${viaje.ruta}/${viaje.fecha}/${viaje.horario}/asistencia"
            val snap = db.reference(path).valueEvents.first()
            if (!snap.exists) return emptyMap()

            val resultado = mutableMapOf<Int, Boolean>()
            snap.children.forEach { child ->
                val asiento = child.key?.toIntOrNull() ?: return@forEach
                val asistio = try { child.child("asistio").value<Boolean>() } catch (e: Exception) { false }
                resultado[asiento] = asistio
            }
            resultado
        } catch (e: Exception) {
            emptyMap()
        }
    }
}